import type { BeforeSendCallback } from "@kalodata/sentry";

/**
 * Sentry 事件过滤与限流。
 *
 * 目标：在不丢失「可操作错误」可见性的前提下结构化降低事件量。所有谓词均为纯函数、可单测。
 * 接线见 src/init.ts；HTTP 层的源头收口见 src/apis/http/sentryPlugin.ts 的 shouldReportHttpError。
 *
 * 线上高量噪音（org kalodata-a5 / kaloclip-web，近 7 天）：
 *  - "Static resource failed to load"：第三方脚本(如 accounts.google.com Google 登录 SDK)加载失败 → isThirdPartyResourceNoise
 *  - UnhandledRejection `{code,message,traceID?}`：legacy axios reject 的业务错误 → isBusinessRejectionNoise
 *  - AbortError（用户主动取消/切页/关 tab 导致的请求取消）→ isAbortNoise
 *  - 重复刷量的同类错误（如 removeChild）→ createSentryRateLimiter
 *  （transport/timeout/abort 在 HTTP 源头 shouldReportHttpError 收口；但绕过 HTTP 链路、由浏览器
 *    全局 onunhandledrejection 直接捕获的 AbortError 不经过该收口，需在此 beforeSend 兜底丢弃。）
 */

type SentryEvent = Parameters<BeforeSendCallback>[0];
type SentryHint = Parameters<BeforeSendCallback>[1];

const STATIC_RESOURCE_MESSAGE = "Static resource failed to load";

const REJECTION_ALLOWED_KEYS = new Set(["code", "message", "traceID", "traceId"]);

const ABORT_ERROR_NAME = "AbortError";
const DOM_EXCEPTION_ABORT_CODE = 20;
const ABORT_MESSAGE_PATTERN = /aborted|abortcontroller|abort error|signal is aborted/i;

/**
 * legacy axios（src/apis/index.ts）reject 的是形如 `{ code, message }` 或
 * `{ code, message, traceID, traceId }` 的普通对象；未被 `.catch()` 的调用方会成为
 * unhandled rejection，被 Sentry 捕获为标题 `<unknown>` 的业务噪音。这类是业务错误而非异常，丢弃。
 */
export const isBusinessRejectionNoise = (_event: SentryEvent, hint?: SentryHint): boolean => {
  const original = hint?.originalException as Record<string, unknown> | null | undefined;
  if (!original || typeof original !== "object" || original instanceof Error) return false;

  const keys = Object.keys(original);
  if (keys.length === 0) return false;
  if (!("code" in original) || !("message" in original)) return false;

  return keys.every((key) => REJECTION_ALLOWED_KEYS.has(key));
};

/**
 * `@kalodata/sentry` 的资源加载监听会把静态资源加载失败上报为 "Static resource failed to load"。
 * 线上绝大多数是第三方脚本(Google 登录 SDK 等)或用户侧网络问题，不可操作，整类丢弃。
 */
export const isThirdPartyResourceNoise = (event: SentryEvent): boolean => {
  return event.message === STATIC_RESOURCE_MESSAGE;
};

/**
 * 用户主动取消请求（切页、关 tab、重复点击触发的 abort、组件卸载 cancel）会抛出 `AbortError`
 * （DOMException.code === 20）。这类是预期行为、不可操作，整类丢弃。
 *
 * HTTP 链路里的 abort 已由 shouldReportHttpError 收口，但绕过该链路、由浏览器全局
 * `onunhandledrejection` 直接捕获的 AbortError（无应用栈帧、handled: no）不经过收口，在此兜底。
 * 既匹配 `hint.originalException`（DOMException / Error，最准），也兜底匹配事件 exception 的
 * type / value（线上存在 `No stacktrace available`、无 originalException 的情况）。
 */
export const isAbortNoise = (event: SentryEvent, hint?: SentryHint): boolean => {
  const original = hint?.originalException as { name?: unknown; code?: unknown; message?: unknown } | null | undefined;
  if (original && typeof original === "object") {
    if (original.name === ABORT_ERROR_NAME) return true;
    if (
      typeof DOMException !== "undefined" &&
      original instanceof DOMException &&
      original.code === DOM_EXCEPTION_ABORT_CODE
    ) {
      return true;
    }
  }

  const exception = event.exception?.values?.[0];
  const type = exception?.type ?? "";
  const value = exception?.value ?? event.message ?? "";
  if (type === ABORT_ERROR_NAME) return true;
  return ABORT_MESSAGE_PATTERN.test(value);
};

/**
 * 事件指纹：错误类型 + API status/code + value 首行（截断）。用于 session 内去重。
 */
export const fingerprintEvent = (event: SentryEvent): string => {
  const exception = event.exception?.values?.[0];
  const type = exception?.type || event.message || "unknown";
  const value = (exception?.value || "").split("\n")[0].slice(0, 120);
  const status = event.tags?.api_status == null ? "" : String(event.tags.api_status);
  const code = event.tags?.api_code == null ? "" : String(event.tags.api_code);
  return `${type}:${status}:${code}:${value}`;
};

/**
 * Session 级去重：同一指纹最多上报 `maxPerFingerprint` 次。
 * 不做随机采样——保证每个 distinct error 可见，只抑制同一错误的重复刷量。
 */
export const createSentryRateLimiter = (options: { maxPerFingerprint: number }) => {
  const counts = new Map<string, number>();
  const { maxPerFingerprint } = options;

  return (event: SentryEvent): boolean => {
    const key = fingerprintEvent(event);
    const next = (counts.get(key) ?? 0) + 1;
    counts.set(key, next);
    return next <= maxPerFingerprint;
  };
};

/**
 * staging 重度采样：按 `rate` 概率保留（仅在 environment === "staging" 时生效）。
 * production 不采样，保留全部 distinct error。
 */
export const createStagingSampler = (rate: number, random: () => number = Math.random) => {
  return (event: SentryEvent): boolean => {
    if (event.environment !== "staging") return true;
    return random() < rate;
  };
};

/**
 * 组合出传给 `initSentry({ beforeSend })` 的回调：
 *   1. staging 采样（production 不影响）
 *   2. 业务 reject 噪音 / 第三方资源加载噪音 / abort 噪音 → 丢弃
 *   3. session 级去重
 */
export const createClipSentryBeforeSend = (options: {
  environment?: string;
  stagingSampleRate: number;
  maxPerFingerprint: number;
  random?: () => number;
}): BeforeSendCallback => {
  const sampleStaging = createStagingSampler(options.stagingSampleRate, options.random);
  const withinRateLimit = createSentryRateLimiter({ maxPerFingerprint: options.maxPerFingerprint });

  return (event, hint) => {
    if (!sampleStaging(event)) return null;
    if (isBusinessRejectionNoise(event, hint)) return null;
    if (isThirdPartyResourceNoise(event)) return null;
    if (isAbortNoise(event, hint)) return null;
    if (!withinRateLimit(event)) return null;
    return event;
  };
};
